import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { KeyRound, Loader2, Plus } from 'lucide-react'
import { usePersonalAccessTokens } from '../hooks'
import { CreateTokenDialog } from './CreateTokenDialog'
import { RevokeTokenDialog } from './RevokeTokenDialog'
import { TokenRevealDialog } from './TokenRevealDialog'
import type { CreatePatResponse } from '@/lib/api/features/personal-access-tokens'
import { useDateTimeFormatters } from '@/lib/i18n/useDateTimeFormatters'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'

export function PersonalAccessTokensCard() {
  const { t } = useTranslation('settings')
  const { smartTime, date: formatDate } = useDateTimeFormatters()
  const tokensQuery = usePersonalAccessTokens()

  const [createOpen, setCreateOpen] = useState(false)
  // Raw token from server, held ONLY in component state, dropped on dialog close.
  // See TokenRevealDialog for the security contract.
  const [revealToken, setRevealToken] = useState<string | null>(null)
  const [revokeTarget, setRevokeTarget] = useState<{ id: string; name: string } | null>(null)

  const tokens = (tokensQuery.data ?? []).filter((t) => t.revokedAt === null)

  const handleCreated = (response: CreatePatResponse) => {
    setCreateOpen(false)
    setRevealToken(response.token)
  }

  const formatExpiry = (expiresAt: string | null) => {
    if (!expiresAt) return t('integrationsPage.tokens.expiresNever')
    const d = new Date(expiresAt)
    if (d.getTime() <= Date.now()) return t('integrationsPage.tokens.expired')
    return formatDate(d, { day: 'numeric', month: 'short', year: 'numeric' })
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <KeyRound className="size-4 text-muted-foreground" />
              <CardTitle>{t('integrationsPage.tokens.title')}</CardTitle>
            </div>
            <CardDescription>
              {t('integrationsPage.tokens.description')}
            </CardDescription>
          </div>
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="size-4 mr-1" />
            {t('integrationsPage.tokens.createButton')}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {tokensQuery.isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="size-5 animate-spin text-muted-foreground" />
          </div>
        ) : tokensQuery.isError ? (
          <div className="flex flex-col items-center gap-3 py-8 text-center">
            <p className="text-sm text-muted-foreground">
              {t('integrationsPage.tokens.loadFailed')}
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => tokensQuery.refetch()}
            >
              {t('integrationsPage.tokens.retry')}
            </Button>
          </div>
        ) : tokens.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            {t('integrationsPage.tokens.empty')}
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('integrationsPage.tokens.columnName')}</TableHead>
                <TableHead>{t('integrationsPage.tokens.columnToken')}</TableHead>
                <TableHead>{t('integrationsPage.tokens.columnCreated')}</TableHead>
                <TableHead>{t('integrationsPage.tokens.columnExpires')}</TableHead>
                <TableHead>{t('integrationsPage.tokens.columnLastUsed')}</TableHead>
                <TableHead className="w-px" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {tokens.map((row) => {
                const expired =
                  row.expiresAt !== null &&
                  new Date(row.expiresAt).getTime() <= Date.now()
                return (
                  <TableRow key={row.id} className={expired ? 'opacity-60' : ''}>
                    <TableCell className="font-medium">{row.name}</TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      mp_••••{row.lastFour}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {smartTime(new Date(row.createdOn))}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatExpiry(row.expiresAt)}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {row.lastUsedAt
                        ? smartTime(new Date(row.lastUsedAt))
                        : t('integrationsPage.tokens.lastUsedNever')}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() =>
                          setRevokeTarget({ id: row.id, name: row.name })
                        }
                      >
                        {t('integrationsPage.tokens.revoke')}
                      </Button>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <CreateTokenDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={handleCreated}
      />
      <TokenRevealDialog
        token={revealToken}
        onClose={() => setRevealToken(null)}
      />
      <RevokeTokenDialog
        tokenId={revokeTarget?.id ?? null}
        tokenName={revokeTarget?.name ?? null}
        onClose={() => setRevokeTarget(null)}
      />
    </Card>
  )
}
